import { useState, useMemo, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { OwnershipFilter } from '@/components/OwnershipFilter';
import { TaskList } from '@/features/tasks/TaskList';
import { CreateTaskSheet } from '@/features/tasks/CreateTaskSheet';
import { TaskDetailSheet } from '@/features/tasks/TaskDetailSheet';
import {
  useListTasks,
  useCompleteTask,
  useCreateTask,
  useUpdateTask,
  useCancelTask,
} from '@/features/tasks/hooks/useTasks';
import type { Task, CreateTaskDto, UpdateTaskDto } from '@/api/tasks';
import type { OwnershipFilterValue } from '@/api/visits';
import { useTaskerTasks } from '@/features/tasks/hooks/useTaskerTasks';
import { TaskerTaskCard } from '@/features/tasks/TaskerTaskCard';
import { Loader2 } from 'lucide-react';

// Shared with VisitsPage for filter synchronization
const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

type FilterValue = OwnershipFilterValue[] | 'all';

function loadOwnershipFilter(): FilterValue {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return ['own'];
}

export function TasksPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [ownershipFilters, setOwnershipFilters] = useState<FilterValue>(loadOwnershipFilter);

  // Persist filter to localStorage (shared with VisitsPage)
  const handleOwnershipChange = useCallback((value: FilterValue) => {
    setOwnershipFilters(value);
    localStorage.setItem(OWNERSHIP_FILTER_KEY, JSON.stringify(value));
  }, []);

  // Convert ownership filters to API string format
  const ownershipFilterStr = useMemo(() => {
    if (ownershipFilters === 'all') return 'all';
    return ownershipFilters.join(',');
  }, [ownershipFilters]);

  // Data fetching with ownership filter
  const { data: tasks, isLoading, isRefetching } = useListTasks(ownershipFilterStr);

  // Tasker tasks (only if configured)
  const { data: taskerTasks = [], isLoading: taskerLoading } = useTaskerTasks();
  const taskerEnabled = !!import.meta.env.VITE_TASKER_API_URL;
  // Filter out terminal (completed) tasker tasks
  const pendingTaskerTasks = taskerTasks.filter((t) => !t.status?.is_terminal);

  // Mutations
  const completeTask = useCompleteTask();
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const cancelTask = useCancelTask();

  const handleComplete = (id: string) => {
    completeTask.mutate(id);
  };

  const handleCreate = (dto: CreateTaskDto) => {
    createTask.mutate(dto, {
      onSuccess: () => {
        setCreateOpen(false);
      },
    });
  };

  const handleUpdate = (id: string, updates: UpdateTaskDto) => {
    updateTask.mutate({ id, updates });
  };

  const handleCancel = (id: string) => {
    cancelTask.mutate(id);
  };

  const handleSelect = (task: Task) => {
    setSelectedTask(task);
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <Header
        title="Задачи"
        rightContent={
          <div className="flex items-center gap-2">
            <OwnershipFilter value={ownershipFilters} onChange={handleOwnershipChange} />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        }
      />

      {/* Task list */}
      <div className="flex-1 overflow-y-auto">
        {/* Tasker tasks first (if configured) */}
        {taskerEnabled && (pendingTaskerTasks.length > 0 || taskerLoading) && (
          <div className="px-4 pt-2 pb-1">
            {taskerLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Tasker...
              </div>
            )}
            {pendingTaskerTasks.length > 0 && (
              <>
                <div className="text-xs text-muted-foreground font-medium pb-1.5">Tasker</div>
                <div className="space-y-2">
                  {pendingTaskerTasks.map((t) => (
                    <TaskerTaskCard key={`t-${t.id}`} task={t} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <TaskList
          tasks={tasks || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          onComplete={handleComplete}
          onSelect={handleSelect}
          completingId={completeTask.isPending ? completeTask.variables : undefined}
        />
      </div>

      {/* Create task sheet */}
      <CreateTaskSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        isCreating={createTask.isPending}
      />

      {/* Task detail sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(open) => !open && setSelectedTask(null)}
        onUpdate={handleUpdate}
        onComplete={handleComplete}
        onCancel={handleCancel}
        isUpdating={updateTask.isPending}
        isCompleting={completeTask.isPending}
        isCancelling={cancelTask.isPending}
      />
    </div>
  );
}
